import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as DB from '@/lib/supabaseDb';
import { formatTRY } from '@/lib/currencyFormat';

interface CustomerSummaryReportProps {
  customerId: string;
  customerName: string;
  customerNo: string;
  onClose: () => void;
}

type DateFilter = 'all' | 'this_month' | 'this_year' | 'custom';

function getDateRange(filter: DateFilter, customStart?: string, customEnd?: string): { start: string | null; end: string | null } {
  const today = new Date();
  const trFormat = (d: Date) => d.toLocaleDateString('en-CA');

  switch (filter) {
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start: trFormat(start), end: trFormat(end) };
    }
    case 'this_year': {
      const start = new Date(today.getFullYear(), 0, 1);
      const end = new Date(today.getFullYear(), 11, 31);
      return { start: trFormat(start), end: trFormat(end) };
    }
    case 'custom':
      return { start: customStart || null, end: customEnd || null };
    default:
      return { start: null, end: null };
  }
}

function statusBadgeClass(status: string): string {
  if (!status) return 'bg-background-100 text-foreground-500';
  const s = status.toLowerCase();
  if (s.includes('ödendi') || s.includes('tamamland') || s.includes('teslim')) return 'bg-emerald-50 text-emerald-700';
  if (s.includes('gecik') || s.includes('gecikmi')) return 'bg-red-50 text-red-700';
  if (s.includes('bekl') || s.includes('hazırl') || s.includes('planland')) return 'bg-amber-50 text-amber-700';
  if (s.includes('iptal') || s.includes('kara')) return 'bg-red-50 text-red-700';
  if (s.includes('aktif') || s.includes('yolda')) return 'bg-blue-50 text-blue-700';
  return 'bg-background-100 text-foreground-500';
}

function SectionTitle({ title, icon }: { title: string; icon: string }) {
  return (
    <div className="flex items-center gap-2 mb-3 print:mb-2 border-b border-background-200 pb-2">
      <span className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-primary-600 text-sm`}></i>
      </span>
      <h3 className="text-base font-semibold text-foreground-900 font-heading">{title}</h3>
    </div>
  );
}

function EmptySection({ message }: { message: string }) {
  return (
    <div className="py-6 text-center">
      <p className="text-sm text-foreground-400 italic">{message}</p>
    </div>
  );
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('tr-TR');
  } catch { return '—'; }
}

function getPaymentLinkedInfo(payment: any, salesMap: Map<string, any>, notesMap: Map<string, string>): string {
  if (payment.sale_id && salesMap.has(payment.sale_id)) {
    return `Satış: ${salesMap.get(payment.sale_id)}`;
  }
  if (payment.promissory_note_id && notesMap.has(payment.promissory_note_id)) {
    return `Senet: ${notesMap.get(payment.promissory_note_id)}`;
  }
  if (payment.source_type === 'legacy_debt') return 'Eski Sistem Borcu';
  if (payment.source_type === 'legacy_opening_balance') return 'Eski Sistem Devir';
  return '—';
}

export default function CustomerSummaryReport({ customerId, customerName, customerNo, onClose }: CustomerSummaryReportProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<DB.CustomerSummaryReport | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [business, setBusiness] = useState<any>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);

  const loadReport = useCallback(async (filter: DateFilter, cStart?: string, cEnd?: string) => {
    setLoading(true);
    setError(null);
    try {
      const { start, end } = getDateRange(filter, cStart, cEnd);
      const [reportRes, bizRes] = await Promise.all([
        DB.getCustomerSummaryReport(customerId, start, end),
        DB.getCurrentBusiness(),
      ]);
      if (reportRes.error) { setError(reportRes.error); return; }
      setReportData(reportRes.data);
      if (bizRes.data) setBusiness(bizRes.data);
    } catch { setError('Rapor yüklenirken hata oluştu.'); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => {
    loadReport(dateFilter, customStart, customEnd);
  }, []);

  useEffect(() => {
    const div = document.createElement('div');
    div.id = 'customer-report-print-root';
    div.style.display = 'none';
    document.body.appendChild(div);
    portalContainerRef.current = div;
    return () => {
      if (div && document.body.contains(div)) {
        document.body.removeChild(div);
      }
    };
  }, []);

  const handleFilterApply = () => {
    setShowDatePicker(false);
    loadReport(dateFilter, customStart, customEnd);
  };

  const handlePrint = () => {
    const printRoot = document.getElementById('customer-report-print-root');
    if (!printRoot) {
      setError('Rapor belgesi hazırlanamadı. Lütfen tekrar deneyin.');
      return;
    }

    // display:none olan elementin offsetHeight'ı her zaman 0 döner.
    // Validasyon için geçici olarak görünür yapıp ölçüm alalım.
    const prevDisplay = printRoot.style.display;
    const prevPosition = printRoot.style.position;
    const prevVisibility = printRoot.style.visibility;
    printRoot.style.display = 'block';
    printRoot.style.position = 'fixed';
    printRoot.style.left = '-9999px';
    printRoot.style.top = '0';
    printRoot.style.visibility = 'hidden';

    // Tarayıcının layout hesaplaması için kısa bir frame bekle
    const rootHeight = printRoot.offsetHeight;
    const textLen = printRoot.textContent?.trim()?.length || 0;

    // Eski haline döndür
    printRoot.style.display = prevDisplay;
    printRoot.style.position = prevPosition;
    printRoot.style.visibility = prevVisibility;
    printRoot.style.left = '';
    printRoot.style.top = '';

    if (textLen < 10 || rootHeight < 50) {
      setError('Rapor belgesi hazırlanamadı. Lütfen tekrar deneyin.');
      return;
    }

    const originalTitle = document.title;
    document.title = `Polatlar-Musteri-Ozet-${customerNo.replace(/[^a-zA-Z0-9-]/g, '')}-${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}`;
    document.fonts.ready.then(() => {
      setTimeout(() => {
        window.print();
        setTimeout(() => { document.title = originalTitle; }, 500);
      }, 100);
    });
  };

  const handlePdfDownload = () => {
    handlePrint();
  };

  const now = new Date();
  const reportDate = now.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const reportTime = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  const reportNo = `RPR-${now.getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;

  // Build lookup maps
  const salesMap = new Map<string, string>();
  const notesLookupMap = new Map<string, string>();
  if (reportData) {
    reportData.sales.forEach((s: any) => salesMap.set(s.id, s.sale_no));
    reportData.promissory_notes.forEach((n: any) => notesLookupMap.set(n.id, n.note_no));
  }

  // Group sale items by sale_id
  const saleItemsBySale: Record<string, any[]> = {};
  if (reportData) {
    reportData.sale_items.forEach((si: any) => {
      if (!saleItemsBySale[si.sale_id]) saleItemsBySale[si.sale_id] = [];
      saleItemsBySale[si.sale_id].push(si);
    });
  }

  const fin = reportData?.financial_summary;
  const cust = reportData?.customer;

  // Report print content
  const reportPrintContent = (
    <div style={{ maxWidth: '210mm', margin: '0 auto', padding: '8mm', fontFamily: 'Inter, system-ui, sans-serif', background: 'white', color: '#1a1a1a', fontSize: '13px' }}>
      {/* Corporate Header */}
      <div style={{ padding: '0 0 12px 0', borderBottom: '2px solid #e5e7eb', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 2px 0' }}>{business?.name || 'Polatlar Mobilya'}</h1>
            <p style={{ fontSize: '11px', color: '#6b7280', margin: '0' }}>Müşteri Hesap Özeti</p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '11px' }}>
            <p style={{ margin: '0 0 2px 0', color: '#6b7280' }}>Rapor No: <strong>{reportNo}</strong></p>
            <p style={{ margin: '0 0 2px 0', color: '#6b7280' }}>Tarih: <strong>{reportDate} {reportTime}</strong></p>
            {business?.phone && <p style={{ margin: '0', color: '#6b7280' }}>Tel: {business.phone}</p>}
            {business?.email && <p style={{ margin: '0', color: '#6b7280' }}>{business.email}</p>}
          </div>
        </div>
      </div>

      {/* Customer Info */}
      {cust && (
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Müşteri Bilgileri</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '2px 6px', width: '20%', color: '#6b7280' }}>Ad Soyad:</td>
                <td style={{ padding: '2px 6px', width: '30%', fontWeight: 600 }}>{cust.full_name}</td>
                <td style={{ padding: '2px 6px', width: '20%', color: '#6b7280' }}>Müşteri No:</td>
                <td style={{ padding: '2px 6px', width: '30%' }}>{cust.customer_no}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Telefon:</td>
                <td style={{ padding: '2px 6px' }}>{cust.phone}</td>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Durum:</td>
                <td style={{ padding: '2px 6px' }}>{cust.status}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>İl / İlçe:</td>
                <td style={{ padding: '2px 6px' }}>{[cust.city, cust.district].filter(Boolean).join(' / ') || '—'}</td>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Kayıt Tarihi:</td>
                <td style={{ padding: '2px 6px' }}>{cust.created_at_formatted}</td>
              </tr>
              {cust.address && (
                <tr>
                  <td style={{ padding: '2px 6px', color: '#6b7280' }}>Adres:</td>
                  <td style={{ padding: '2px 6px' }} colSpan={3}>{cust.address}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Financial Summary */}
      {fin && (
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Finansal Özet</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Toplam Alışveriş:</td>
                <td style={{ padding: '2px 6px', fontWeight: 600 }}>{formatTRY(fin.total_alısveris)}</td>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Tahsil Edilen:</td>
                <td style={{ padding: '2px 6px', fontWeight: 600 }}>{formatTRY(fin.total_paid)}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Güncel Kalan Borç:</td>
                <td style={{ padding: '2px 6px', fontWeight: 600, color: fin.current_debt > 0 ? '#ef4444' : '#059669' }}>{formatTRY(fin.current_debt)}</td>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Gecikmiş Borç:</td>
                <td style={{ padding: '2px 6px', fontWeight: 600, color: fin.overdue_debt > 0 ? '#ef4444' : '#374151' }}>{formatTRY(fin.overdue_debt)}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Açık Senet:</td>
                <td style={{ padding: '2px 6px', fontWeight: 600 }}>{formatTRY(fin.open_notes_total)}</td>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Açık Taksit:</td>
                <td style={{ padding: '2px 6px', fontWeight: 600 }}>{formatTRY(fin.open_installments_total)}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>İcra Alacak:</td>
                <td style={{ padding: '2px 6px', fontWeight: 600, color: fin.enforcement_debt > 0 ? '#ef4444' : '#374151' }}>{formatTRY(fin.enforcement_debt)}</td>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Son Ödeme:</td>
                <td style={{ padding: '2px 6px' }}>{formatDate(fin.last_payment_date)}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 6px', color: '#6b7280' }}>Son Alışveriş:</td>
                <td style={{ padding: '2px 6px' }}>{formatDate(fin.last_purchase_date)}</td>
                <td></td><td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Sales Table */}
      {reportData && reportData.sales.length > 0 && (
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Satışlar</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>No</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Tarih</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Ürünler</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Adet</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Tutar</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Ödenen</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Kalan</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Ödeme Tipi</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Teslimat</th>
              </tr>
            </thead>
            <tbody>
              {reportData.sales.map((sale: any) => {
                const items = saleItemsBySale[sale.id] || [];
                const totalQty = items.reduce((sum: number, si: any) => sum + (si.quantity || 0), 0);
                return (
                  <tr key={sale.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '3px 4px', fontWeight: 600 }}>{sale.sale_no}</td>
                    <td style={{ padding: '3px 4px', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDate(sale.sale_date)}</td>
                    <td style={{ padding: '3px 4px', color: '#6b7280', maxWidth: '140px' }}>{items.map((si: any) => si.product_name).join(', ')}</td>
                    <td style={{ padding: '3px 4px', textAlign: 'right', color: '#374151' }}>{totalQty}</td>
                    <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600 }}>{formatTRY(sale.total_amount)}</td>
                    <td style={{ padding: '3px 4px', textAlign: 'right', color: '#059669' }}>{formatTRY(sale.paid_amount)}</td>
                    <td style={{ padding: '3px 4px', textAlign: 'right', color: '#ef4444' }}>{formatTRY(sale.remaining_amount)}</td>
                    <td style={{ padding: '3px 4px', color: '#6b7280' }}>{sale.payment_type || '—'}</td>
                    <td style={{ padding: '3px 4px', color: '#6b7280' }}>{sale.delivery_status || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Installments */}
      {reportData && reportData.installments.length > 0 && (
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Taksit Planı</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>#</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Satış</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Vade</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Tutar</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Ödenen</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Kalan</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {reportData.installments.map((inst: any) => (
                <tr key={inst.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 4px', fontWeight: 600 }}>{inst.installment_no ? `#${inst.installment_no}` : `#${inst.sequence_no || '—'}`}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{inst.linked_sale_no || '—'}</td>
                  <td style={{ padding: '3px 4px', color: '#374151', whiteSpace: 'nowrap' }}>{formatDate(inst.due_date)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600 }}>{formatTRY(inst.amount)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', color: '#059669' }}>{formatTRY(inst.paid_amount)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', color: '#ef4444' }}>{formatTRY(inst.remaining_amount)}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{inst.status || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Promissory Notes */}
      {reportData && reportData.promissory_notes.length > 0 && (
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Senetler</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>No</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Tutar</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Ödenen</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Kalan</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Vade</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Durum</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>İcra</th>
              </tr>
            </thead>
            <tbody>
              {reportData.promissory_notes.map((note: any) => (
                <tr key={note.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 4px', fontWeight: 600 }}>{note.note_no}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right' }}>{formatTRY(note.amount)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', color: '#059669' }}>{formatTRY(note.paid_amount)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', color: '#ef4444' }}>{formatTRY(note.remaining_amount)}</td>
                  <td style={{ padding: '3px 4px', color: '#374151', whiteSpace: 'nowrap' }}>{formatDate(note.due_date)}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{note.status || '—'}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{note.enforcement_status ? 'İcrada' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payments */}
      {reportData && reportData.payments.length > 0 && (
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Ödemeler</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Makbuz</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Tarih</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Tutar</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Yöntem</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Bağlantı</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Açıklama</th>
              </tr>
            </thead>
            <tbody>
              {reportData.payments.map((pay: any) => (
                <tr key={pay.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 4px', fontWeight: 600 }}>{pay.receipt_no || '—'}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDate(pay.payment_date)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatTRY(pay.amount)}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{pay.payment_method || '—'}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280', fontSize: '9px' }}>{getPaymentLinkedInfo(pay, salesMap, notesLookupMap)}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280', fontSize: '9px' }}>{pay.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Promises */}
      {reportData && reportData.payment_promises.length > 0 && (
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Ödeme Sözleri</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Söz Tarihi</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Tutar</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Durum</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Sonraki Arama</th>
              </tr>
            </thead>
            <tbody>
              {reportData.payment_promises.map((pp: any) => (
                <tr key={pp.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 4px', color: '#374151', whiteSpace: 'nowrap' }}>{formatDate(pp.promised_date)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600 }}>{formatTRY(pp.promised_amount)}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{pp.follow_up_status || pp.status || '—'}</td>
                  <td style={{ padding: '3px 4px', color: '#374151', whiteSpace: 'nowrap' }}>{formatDate(pp.next_follow_up_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Deliveries */}
      {reportData && reportData.deliveries.length > 0 && (
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Teslimatlar</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Satış</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Tarih</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Durum</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Adres</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Personel</th>
              </tr>
            </thead>
            <tbody>
              {reportData.deliveries.map((del: any) => (
                <tr key={del.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{del.linked_sale_no || '—'}</td>
                  <td style={{ padding: '3px 4px', color: '#374151', whiteSpace: 'nowrap' }}>{formatDate(del.delivery_date)}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{del.status || '—'}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280', fontSize: '9px' }}>{del.address || '—'}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{del.delivery_person || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Enforcement */}
      {reportData && reportData.enforcement_cases.length > 0 && (
        <div style={{ paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>İcra Bilgileri</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Dosya No</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Ana Borç</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Tahsil</th>
                <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600 }}>Kalan</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Durum</th>
                <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600 }}>Son İşlem</th>
              </tr>
            </thead>
            <tbody>
              {reportData.enforcement_cases.map((enf: any) => (
                <tr key={enf.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 4px', fontWeight: 600 }}>{enf.case_no}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right' }}>{formatTRY(enf.total_debt)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', color: '#059669' }}>{formatTRY(enf.paid_after_enforcement)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', color: '#ef4444' }}>{formatTRY(enf.remaining_debt)}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{enf.status || '—'}</td>
                  <td style={{ padding: '3px 4px', color: '#374151', whiteSpace: 'nowrap' }}>{formatDate(enf.last_contact_date || enf.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <div style={{ paddingTop: '10px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
        <p style={{ fontSize: '10px', color: '#9ca3af', margin: '0' }}>{business?.name || 'Polatlar Mobilya'} · Müşteri Hesap Özeti · {reportNo}</p>
      </div>
    </div>
  );

  return (
    <>
      {/* Print portal */}
      {portalContainerRef.current && reportData && createPortal(reportPrintContent, portalContainerRef.current)}

      {/* Backdrop + Modal */}
      <div className="no-print fixed inset-0 bg-black/40 z-[80] animate-fade-in" onClick={onClose} />
      <div className="no-print fixed inset-4 lg:inset-x-12 lg:inset-y-6 z-[81] bg-background-50 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slide-up">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-background-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-foreground-900 font-heading">Müşteri Özet Raporu</h2>
            <p className="text-xs text-foreground-400">{customerName} · {customerNo}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Date Filter */}
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-background-200 text-sm text-foreground-600 hover:bg-background-50 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-line"></i>
                <span>{dateFilter === 'all' ? 'Tüm Zamanlar' : dateFilter === 'this_month' ? 'Bu Ay' : dateFilter === 'this_year' ? 'Bu Yıl' : 'Özel Aralık'}</span>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              {showDatePicker && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-background-50 rounded-xl border border-background-200 shadow-lg z-[90] p-3">
                  <div className="space-y-1.5">
                    {([
                      { key: 'all', label: 'Tüm Zamanlar' },
                      { key: 'this_month', label: 'Bu Ay' },
                      { key: 'this_year', label: 'Bu Yıl' },
                      { key: 'custom', label: 'Özel Tarih Aralığı' },
                    ] as const).map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => setDateFilter(opt.key as DateFilter)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors ${
                          dateFilter === opt.key ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {dateFilter === 'custom' && (
                    <div className="mt-3 space-y-2">
                      <div>
                        <label className="text-xs text-foreground-400 mb-0.5 block">Başlangıç</label>
                        <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-background-200 text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-foreground-400 mb-0.5 block">Bitiş</label>
                        <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-background-200 text-sm" />
                      </div>
                    </div>
                  )}
                  <button onClick={handleFilterApply} className="mt-3 w-full btn-primary text-sm cursor-pointer whitespace-nowrap">
                    Uygula
                  </button>
                </div>
              )}
            </div>
            {/* Action buttons */}
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-background-200 text-sm text-foreground-600 hover:bg-background-50 cursor-pointer whitespace-nowrap">
              <i className="ri-printer-line"></i><span>Yazdır</span>
            </button>
            <button onClick={handlePdfDownload} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-500 text-background-50 text-sm hover:bg-accent-600 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line"></i><span>PDF İndir</span>
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer">
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>
        </div>

        {/* Scrollable Report Content */}
        <div className="flex-1 overflow-y-auto bg-background-100/50">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
                <p className="text-sm text-foreground-400">Rapor hazırlanıyor...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full px-4">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-3">
                <i className="ri-error-warning-line text-xl text-red-400"></i>
              </div>
              <p className="text-sm text-foreground-500">{error}</p>
              <button onClick={() => loadReport(dateFilter, customStart, customEnd)} className="btn-primary mt-3 text-sm cursor-pointer">Tekrar Dene</button>
            </div>
          ) : (
            <div ref={reportRef} className="report-print-area max-w-[210mm] mx-auto bg-background-50 shadow-sm print:shadow-none print:max-w-none print:mx-0">
              {/* ===== 1. KURUMSAL ÜST ALAN ===== */}
              <div className="px-6 lg:px-10 pt-6 pb-4 border-b-2 border-primary-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                      <i className="ri-store-2-line text-2xl text-primary-600"></i>
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-foreground-900 font-heading">{business?.name || 'Polatlar Mobilya'}</h1>
                      <p className="text-xs text-foreground-400">Müşteri Hesap Özeti</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-foreground-400">Rapor No: <strong className="text-foreground-700">{reportNo}</strong></p>
                    <p className="text-xs text-foreground-400">Tarih: <strong className="text-foreground-700">{reportDate} {reportTime}</strong></p>
                    {business?.phone && <p className="text-xs text-foreground-400">Tel: {business.phone}</p>}
                    {business?.email && <p className="text-xs text-foreground-400">{business.email}</p>}
                  </div>
                </div>
              </div>

              {/* ===== 2. MÜŞTERİ BİLGİLERİ ===== */}
              {cust && (
                <div className="px-6 lg:px-10 py-4 border-b border-background-200">
                  <SectionTitle title="Müşteri Bilgileri" icon="ri-user-line" />
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                    <InfoRow label="Ad Soyad" value={cust.full_name} />
                    <InfoRow label="Müşteri No" value={cust.customer_no} />
                    <InfoRow label="Telefon" value={cust.phone} />
                    {cust.second_phone && <InfoRow label="2. Telefon" value={cust.second_phone} />}
                    {cust.identity_number && <InfoRow label="TC Kimlik No" value={cust.identity_number} />}
                    <InfoRow label="İl" value={cust.city} />
                    <InfoRow label="İlçe" value={cust.district} />
                    <InfoRow label="Durum" value={cust.status} badge />
                    <InfoRow label="Kayıt Tarihi" value={cust.created_at_formatted} />
                  </div>
                  {cust.address && (
                    <div className="mt-2 text-sm">
                      <span className="text-foreground-400">Adres: </span>
                      <span className="text-foreground-700">{cust.address}</span>
                    </div>
                  )}
                </div>
              )}

              {/* ===== 3. FİNANSAL ÖZET ===== */}
              {fin && (
                <div className="px-6 lg:px-10 py-4 border-b border-background-200">
                  <SectionTitle title="Finansal Özet" icon="ri-money-dollar-circle-line" />
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    <SummaryCard label="Toplam Alışveriş" value={formatTRY(fin.total_alısveris)} />
                    <SummaryCard label="Toplam Tahsil Edilen" value={formatTRY(fin.total_paid)} color="emerald" />
                    <SummaryCard label="Güncel Kalan Borç" value={formatTRY(fin.current_debt)} color={fin.current_debt > 0 ? 'red' : 'emerald'} />
                    <SummaryCard label="Gecikmiş Borç" value={formatTRY(fin.overdue_debt)} color={fin.overdue_debt > 0 ? 'red' : 'default'} />
                    <SummaryCard label="Açık Senet Toplamı" value={formatTRY(fin.open_notes_total)} />
                    <SummaryCard label="Açık Taksit Toplamı" value={formatTRY(fin.open_installments_total)} />
                    <SummaryCard label="İcradaki Alacak" value={formatTRY(fin.enforcement_debt)} color={fin.enforcement_debt > 0 ? 'red' : 'default'} />
                    <SummaryCard label="Son Ödeme Tarihi" value={formatDate(fin.last_payment_date)} />
                    <SummaryCard label="Son Alışveriş Tarihi" value={formatDate(fin.last_purchase_date)} />
                  </div>
                </div>
              )}

              {/* ===== 4. SATIŞLAR ===== */}
              {reportData && (
                <div className="px-6 lg:px-10 py-4 border-b border-background-200">
                  <SectionTitle title="Satışlar" icon="ri-shopping-cart-2-line" />
                  {reportData.sales.length === 0 ? (
                    <EmptySection message="Bu dönemde satış kaydı bulunmamaktadır." />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-background-200">
                            <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Satış No</th>
                            <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Tarih</th>
                            <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Ürünler</th>
                            <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Adet</th>
                            <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Tutar</th>
                            <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Ödenen</th>
                            <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Kalan</th>
                            <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Ödeme Tipi</th>
                            <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Teslimat</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.sales.map((sale: any) => {
                            const items = saleItemsBySale[sale.id] || [];
                            const totalQty = items.reduce((sum: number, si: any) => sum + (si.quantity || 0), 0);
                            return (
                              <>
                                <tr key={sale.id} className="border-b border-background-100 hover:bg-background-50/50">
                                  <td className="py-2 px-2 font-medium text-foreground-800">{sale.sale_no}</td>
                                  <td className="py-2 px-2 text-foreground-600 whitespace-nowrap">{formatDate(sale.sale_date)}</td>
                                  <td className="py-2 px-2 text-foreground-600 max-w-[160px] truncate" title={items.map((si: any) => si.product_name).join(', ')}>
                                    {items.map((si: any) => si.product_name).join(', ')}
                                  </td>
                                  <td className="py-2 px-2 text-right text-foreground-600">{totalQty}</td>
                                  <td className="py-2 px-2 text-right font-medium text-foreground-800">{formatTRY(sale.total_amount)}</td>
                                  <td className="py-2 px-2 text-right text-emerald-600">{formatTRY(sale.paid_amount)}</td>
                                  <td className="py-2 px-2 text-right text-red-600">{formatTRY(sale.remaining_amount)}</td>
                                  <td className="py-2 px-2 text-foreground-600">{sale.payment_type || '—'}</td>
                                  <td className="py-2 px-2">
                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(sale.delivery_status)}`}>
                                      {sale.delivery_status || '—'}
                                    </span>
                                  </td>
                                </tr>
                                {/* Product details sub-rows */}
                                {items.map((si: any) => (
                                  <tr key={si.id} className="border-b border-background-100/50 bg-background-50/30">
                                    <td className="py-1.5 px-2"></td>
                                    <td className="py-1.5 px-2 text-xs text-foreground-400" colSpan={2}>
                                      <span className="text-foreground-500 font-medium">{si.product_name}</span>
                                      {si.brand && <span className="ml-1">· {si.brand}</span>}
                                      {si.model_code && <span className="ml-1">· {si.model_code}</span>}
                                    </td>
                                    <td className="py-1.5 px-2 text-right text-xs text-foreground-500">{si.quantity}</td>
                                    <td className="py-1.5 px-2 text-right text-xs text-foreground-500">{formatTRY(si.total_price)}</td>
                                    <td className="py-1.5 px-2"></td>
                                    <td className="py-1.5 px-2"></td>
                                    <td className="py-1.5 px-2 text-xs text-foreground-400">{si.product_type || ''}</td>
                                    <td className="py-1.5 px-2"></td>
                                  </tr>
                                ))}
                              </>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ===== 5. TAKSİT PLANI ===== */}
              {reportData && reportData.installments.length > 0 && (
                <div className="px-6 lg:px-10 py-4 border-b border-background-200">
                  <SectionTitle title="Taksit Planı" icon="ri-calendar-check-line" />
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200">
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Taksit No</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Satış</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Vade</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Tutar</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Ödenen</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Kalan</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.installments.map((inst: any) => (
                          <tr key={inst.id} className="border-b border-background-100 hover:bg-background-50/50">
                            <td className="py-2 px-2 font-medium text-foreground-800 whitespace-nowrap">
                              {inst.installment_no ? `#${inst.installment_no}` : `#${inst.sequence_no || '—'}`}
                            </td>
                            <td className="py-2 px-2 text-foreground-500 text-xs">{inst.linked_sale_no || '—'}</td>
                            <td className="py-2 px-2 text-foreground-600 whitespace-nowrap">{formatDate(inst.due_date)}</td>
                            <td className="py-2 px-2 text-right text-foreground-700">{formatTRY(inst.amount)}</td>
                            <td className="py-2 px-2 text-right text-emerald-600">{formatTRY(inst.paid_amount)}</td>
                            <td className="py-2 px-2 text-right text-red-600">{formatTRY(inst.remaining_amount)}</td>
                            <td className="py-2 px-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(inst.status)}`}>
                                {inst.status || '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ===== 6. SENETLER ===== */}
              {reportData && reportData.promissory_notes.length > 0 && (
                <div className="px-6 lg:px-10 py-4 border-b border-background-200">
                  <SectionTitle title="Senetler" icon="ri-file-list-3-line" />
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200">
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Senet No</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Tutar</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Ödenen</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Kalan</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Vade</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Durum</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Kaynak</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">İcra</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.promissory_notes.map((note: any) => (
                          <tr key={note.id} className="border-b border-background-100 hover:bg-background-50/50">
                            <td className="py-2 px-2 font-medium text-foreground-800 whitespace-nowrap">{note.note_no}</td>
                            <td className="py-2 px-2 text-right text-foreground-700">{formatTRY(note.amount)}</td>
                            <td className="py-2 px-2 text-right text-emerald-600">{formatTRY(note.paid_amount)}</td>
                            <td className="py-2 px-2 text-right text-red-600">{formatTRY(note.remaining_amount)}</td>
                            <td className="py-2 px-2 text-foreground-600 whitespace-nowrap">{formatDate(note.due_date)}</td>
                            <td className="py-2 px-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(note.status)}`}>
                                {note.status || '—'}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-xs text-foreground-500">
                              {note.source_type === 'legacy_opening_balance' ? 'Eski Sistem Devir' : note.sale_id ? 'Satışa Bağlı' : 'Bağımsız'}
                            </td>
                            <td className="py-2 px-2">
                              {note.enforcement_status ? (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">İcrada</span>
                              ) : (
                                <span className="text-xs text-foreground-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ===== 7. ÖDEMELER ===== */}
              {reportData && reportData.payments.length > 0 && (
                <div className="px-6 lg:px-10 py-4 border-b border-background-200">
                  <SectionTitle title="Ödemeler" icon="ri-bank-card-line" />
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200">
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Makbuz No</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Tarih</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Tutar</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Yöntem</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Bağlantı</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Personel</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Açıklama</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.payments.map((pay: any) => (
                          <tr key={pay.id} className="border-b border-background-100 hover:bg-background-50/50">
                            <td className="py-2 px-2 font-medium text-foreground-800 whitespace-nowrap">{pay.receipt_no || '—'}</td>
                            <td className="py-2 px-2 text-foreground-600 whitespace-nowrap">{formatDate(pay.payment_date)}</td>
                            <td className="py-2 px-2 text-right font-medium text-emerald-600">{formatTRY(pay.amount)}</td>
                            <td className="py-2 px-2 text-foreground-600">{pay.payment_method || '—'}</td>
                            <td className="py-2 px-2 text-xs text-foreground-500">{getPaymentLinkedInfo(pay, salesMap, notesLookupMap)}</td>
                            <td className="py-2 px-2 text-foreground-600">{pay.received_by || '—'}</td>
                            <td className="py-2 px-2 text-foreground-500 text-xs max-w-[120px] truncate" title={pay.description}>{pay.description || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ===== 8. ÖDEME SÖZLERİ ===== */}
              {reportData && reportData.payment_promises.length > 0 && (
                <div className="px-6 lg:px-10 py-4 border-b border-background-200">
                  <SectionTitle title="Ödeme Sözleri" icon="ri-hand-heart-line" />
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200">
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Söz Tarihi</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Tutar</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Takip Durumu</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Son Görüşme Notu</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Sonraki Arama</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.payment_promises.map((pp: any) => (
                          <tr key={pp.id} className="border-b border-background-100 hover:bg-background-50/50">
                            <td className="py-2 px-2 text-foreground-600 whitespace-nowrap">{formatDate(pp.promised_date)}</td>
                            <td className="py-2 px-2 text-right font-medium text-foreground-800">{formatTRY(pp.promised_amount)}</td>
                            <td className="py-2 px-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(pp.follow_up_status || pp.status)}`}>
                                {pp.follow_up_status || pp.status || '—'}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-foreground-500 text-xs max-w-[160px] truncate" title={pp.last_contact_note}>{pp.last_contact_note || '—'}</td>
                            <td className="py-2 px-2 text-foreground-600 whitespace-nowrap">{formatDate(pp.next_follow_up_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ===== 9. TESLİMATLAR ===== */}
              {reportData && reportData.deliveries.length > 0 && (
                <div className="px-6 lg:px-10 py-4 border-b border-background-200">
                  <SectionTitle title="Teslimatlar" icon="ri-truck-line" />
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200">
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Satış</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Teslimat Tarihi</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Durum</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Adres</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Personel</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Not</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.deliveries.map((del: any) => (
                          <tr key={del.id} className="border-b border-background-100 hover:bg-background-50/50">
                            <td className="py-2 px-2 text-foreground-500 text-xs">{del.linked_sale_no || '—'}</td>
                            <td className="py-2 px-2 text-foreground-600 whitespace-nowrap">{formatDate(del.delivery_date)}</td>
                            <td className="py-2 px-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(del.status)}`}>
                                {del.status || '—'}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-foreground-500 text-xs max-w-[140px] truncate" title={del.address}>{del.address || '—'}</td>
                            <td className="py-2 px-2 text-foreground-600">{del.delivery_person || '—'}</td>
                            <td className="py-2 px-2 text-foreground-500 text-xs max-w-[100px] truncate" title={del.notes}>{del.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ===== 10. İCRA BİLGİLERİ ===== */}
              {reportData && reportData.enforcement_cases.length > 0 && (
                <div className="px-6 lg:px-10 py-4 border-b border-background-200">
                  <SectionTitle title="İcra Bilgileri" icon="ri-scales-3-line" />
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200">
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Dosya No</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Ana Borç</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Tahsil Edilen</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Kalan</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Durum</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-foreground-400 uppercase">Son İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.enforcement_cases.map((enf: any) => (
                          <tr key={enf.id} className="border-b border-background-100 hover:bg-background-50/50">
                            <td className="py-2 px-2 font-medium text-foreground-800 whitespace-nowrap">{enf.case_no}</td>
                            <td className="py-2 px-2 text-right text-foreground-700">{formatTRY(enf.total_debt)}</td>
                            <td className="py-2 px-2 text-right text-emerald-600">{formatTRY(enf.paid_after_enforcement)}</td>
                            <td className="py-2 px-2 text-right text-red-600">{formatTRY(enf.remaining_debt)}</td>
                            <td className="py-2 px-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(enf.status)}`}>
                                {enf.status || '—'}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-foreground-600 whitespace-nowrap">{formatDate(enf.last_contact_date || enf.updated_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="px-6 lg:px-10 py-3 text-center text-xs text-foreground-300 print:fixed print:bottom-0 print:w-full">
                Polatlar Mobilya · Müşteri Hesap Özeti · {reportNo}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* Helper components */
function InfoRow({ label, value, badge }: { label: string; value: string | null | undefined; badge?: boolean }) {
  return (
    <div>
      <span className="text-foreground-400 text-xs">{label}</span>
      <p className="text-foreground-700 font-medium">
        {badge && value ? (
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(value)}`}>{value}</span>
        ) : (
          value || '—'
        )}
      </p>
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string; color?: string }) {
  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-50 border-emerald-100',
    red: 'bg-red-50 border-red-100',
    default: 'bg-background-100 border-background-200',
  };
  const textColorMap: Record<string, string> = {
    emerald: 'text-emerald-700',
    red: 'text-red-700',
    default: 'text-foreground-800',
  };
  const bgClass = colorMap[color || 'default'];
  const textClass = textColorMap[color || 'default'];

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${bgClass}`}>
      <p className="text-xs text-foreground-400 mb-0.5">{label}</p>
      <p className={`text-sm font-bold ${textClass}`}>{value}</p>
    </div>
  );
}